import { GridApi } from '@ag-grid-community/core';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { NotificationEventType } from 'src/entities/Notifications';
import AgGrid, {
  AgGridContext,
  UserCellRenderer,
} from 'src/legacy/components/AgGrid';

import { BatchActionsToolbar } from '../Files/FilesTable/BatchActionsToolbar';
import CheckboxCellRenderer from '../Files/FilesTable/components/CheckboxCellRenderer';
import HeaderCheckboxRenderer from '../Files/FilesTable/components/HeaderCheckboxRenderer';
import { getHumanReadableFileSize } from '../Files/helpers';
import BaseTypography from '../Text/BaseTypography';

import { NotificationTableRowData } from './InboxContent';
import { NotificationTableActionRenderer } from './NotificationTableActionRenderer';
import { NotificationTableBreadcrumbRenderer } from './NotificationTableBreadcrumbRenderer';
import { NotificationTableNameRenderer } from './NotificationTableNameRenderer';

interface BulkActionsConfig {
  selectedRowIds: Set<string>;
  showDeleteAction: boolean;
  onToggleRow: (rowId: string) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onDownload: () => void;
  onDelete: () => void;
}

interface InboxNotificationTableProps {
  rowData: NotificationTableRowData[];
  notificationEventType: NotificationEventType;
  bulkActions?: BulkActionsConfig;
}

const FileSizeRenderer = ({ value: fileSize }: { value: number | undefined }) =>
  // file size can be undefined for deleted files - then we render nothing
  fileSize ? (
    <BaseTypography>{getHumanReadableFileSize(fileSize)}</BaseTypography>
  ) : null;

interface InboxCheckboxCellRendererProps {
  data: NotificationTableRowData;
  getIsSelected: (rowId: string) => boolean;
  onToggle: (rowId: string) => void;
}

const InboxCheckboxCellRenderer: React.FC<InboxCheckboxCellRendererProps> = ({
  data,
  getIsSelected,
  onToggle,
}) => {
  if (data.isDeleted) return null;
  return (
    <CheckboxCellRenderer
      isSelected={getIsSelected(data.rowId)}
      onToggle={() => onToggle(data.rowId)}
    />
  );
};

const useStyles = makeStyles()((theme) => ({
  tableWrapper: {
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      // intiallay hide the notification table row action button
      '& .notification-table-action': {
        display: 'none',
      },
      // display the notification table row action button while row hover
      '& .ag-row-hover .notification-table-action': {
        display: 'initial',
      },
    },
  },
}));

export const InboxNotificationTable = ({
  rowData = [],
  notificationEventType,
  bulkActions,
}: InboxNotificationTableProps) => {
  const { classes } = useStyles();
  const [gridApi, setGridApi] = React.useState<GridApi>();

  const agGridContextValue = React.useMemo(
    () => ({
      gridApi,
      setGridApi,
    }),
    [gridApi, setGridApi],
  );

  const sortComparator = (valA: string, valB: string) => {
    if (!valA || !valB) {
      return 0;
    }

    return valA > valB ? 1 : -1;
  };

  const isFileNotification =
    notificationEventType === 'files.new' ||
    notificationEventType === 'files.created';
  const enableBulkActions = Boolean(bulkActions) && isFileNotification;

  const selectableRowCount = React.useMemo(
    () => rowData.filter((row) => !row.isDeleted).length,
    [rowData],
  );

  const selectionCount = bulkActions?.selectedRowIds.size ?? 0;
  const isAllSelected =
    selectableRowCount > 0 && selectionCount === selectableRowCount;
  const isIndeterminate =
    selectionCount > 0 && selectionCount < selectableRowCount;
  const hasSelection = selectionCount > 0;

  // Refs keep header/cell renderer params referentially stable so AG-Grid
  // doesn't rebuild the column defs on every selection change; renderers read
  // the latest state via the *getter* callbacks below on each refresh.
  const bulkStateRef = React.useRef({
    isAllSelected,
    isIndeterminate,
    hasSelection,
  });
  bulkStateRef.current = { isAllSelected, isIndeterminate, hasSelection };

  const bulkActionsRef = React.useRef(bulkActions);
  bulkActionsRef.current = bulkActions;

  const stableGetBulkState = React.useCallback(() => bulkStateRef.current, []);

  const stableSelectAll = React.useCallback(() => {
    bulkActionsRef.current?.onSelectAll();
  }, []);

  const stableDeselectAll = React.useCallback(() => {
    bulkActionsRef.current?.onClear();
  }, []);

  const stableGetIsSelected = React.useCallback(
    (rowId: string) =>
      bulkActionsRef.current?.selectedRowIds.has(rowId) ?? false,
    [],
  );

  const stableOnToggleRow = React.useCallback((rowId: string) => {
    bulkActionsRef.current?.onToggleRow(rowId);
  }, []);

  React.useEffect(() => {
    if (gridApi) {
      gridApi.refreshHeader();
      gridApi.refreshCells({ columns: ['checkbox'], force: true });
    }
  }, [gridApi, hasSelection, isAllSelected, isIndeterminate, selectionCount]);

  const columns = React.useMemo(
    () => [
      ...(enableBulkActions
        ? [
            {
              colId: 'checkbox',
              headerName: '',
              headerClass: 'bulk-checkbox-header',
              headerComponentFramework: HeaderCheckboxRenderer,
              headerComponentParams: {
                getBulkState: stableGetBulkState,
                onSelectAll: stableSelectAll,
                onDeselectAll: stableDeselectAll,
              },
              width: 36,
              minWidth: 36,
              maxWidth: 36,
              suppressSizeToFit: true,
              sortable: false,
              suppressMovable: true,
              cellRenderer: 'inboxCheckboxRenderer',
              cellRendererParams: {
                getIsSelected: stableGetIsSelected,
                onToggle: stableOnToggleRow,
              },
              cellClass: 'bulk-checkbox-column',
            },
          ]
        : []),
      {
        headerName: 'Name',
        field: 'name',
        suppressSizeToFit: true,
        sortable: true,
        sort: 'asc' as const,
        cellRenderer: 'nameCellRenderer',
        comparator: sortComparator,
      },
      {
        headerName: 'Location',
        field: 'location',
        minWidth: 300,
        sortable: true,
        comparator: sortComparator,
        cellRenderer: 'fileLocationRenderer',
        cellRendererParams: {
          useCellData: true,
        },
      },
      isFileNotification
        ? {
            headerName: 'Size',
            field: 'size',
            sortable: true,
            minWidth: 100,
            comparator: sortComparator,
            cellRenderer: 'sizeCellRenderer',
            cellRendererParams: {
              useCellData: true,
            },
          }
        : {},
      {
        headerName: isFileNotification ? 'From' : 'Signed by',
        field: 'userName',
        cellRenderer: 'userCellRenderer',
        minWidth: 280,
        cellRendererParams: {
          useCellData: true,
          fieldKeys: {
            nameField: 'userName',
            avatarField: 'picture',
            hideCellField: 'isDeleted', // we don't want to show User for deleted files
          },
        },
        sortable: true,
        comparator: sortComparator,
      },
      {
        colId: 'actions',
        headerName: 'Actions',
        cellRenderer: 'actionRenderer',
        suppressSizeToFit: true,
        width: 120,
        cellRendererParams: {
          useCellData: true,
        },
        cellClass: 'centeredColumn',
        pinned: 'right',
      },
    ],
    [
      enableBulkActions,
      isFileNotification,
      stableDeselectAll,
      stableGetBulkState,
      stableGetIsSelected,
      stableOnToggleRow,
      stableSelectAll,
    ],
  );

  if (!rowData.length) {
    return null;
  }

  return (
    <div
      className={`${classes.tableWrapper} relative${
        enableBulkActions
          ? ` bulk-enabled-table${hasSelection ? ' bulk-has-selection' : ''}`
          : ''
      }`}
      data-testid="notification-table"
    >
      <AgGridContext.Provider value={agGridContextValue}>
        {enableBulkActions && bulkActions && (
          <BatchActionsToolbar
            selectionCount={selectionCount}
            showDeleteAction={bulkActions.showDeleteAction}
            onDownload={bulkActions.onDownload}
            onDelete={bulkActions.onDelete}
            onClear={stableDeselectAll}
            onSelectAll={stableSelectAll}
            isAllSelected={isAllSelected}
            isIndeterminate={isIndeterminate}
          />
        )}
        <AgGrid
          rowData={rowData}
          columnDefs={columns}
          overlayParams={{
            noRowsLabel: 'No notification',
          }}
          getRowNodeId={(row: NotificationTableRowData) => row.rowId}
          frameworkComponents={{
            nameCellRenderer: NotificationTableNameRenderer,
            userCellRenderer: UserCellRenderer,
            fileLocationRenderer: NotificationTableBreadcrumbRenderer,
            actionRenderer: NotificationTableActionRenderer,
            sizeCellRenderer: FileSizeRenderer,
            inboxCheckboxRenderer: InboxCheckboxCellRenderer,
          }}
          immutableData
        />
      </AgGridContext.Provider>
    </div>
  );
};
